import type { MechanicDefinition, MechanicConfig, MechanicCategory, MechanicSubGroup, WidgetDefinition, WidgetLocation, MechanicEvent, EventReaction } from "./types";
import { timePassage } from "./time-passage";
import { skillChecks } from "./skill-checks";
import { combat } from "./combat";
import { inventory } from "./inventory";
import { reputation } from "./reputation";
import { magicSystem } from "./magic-system";
import { survival } from "./survival";
import { questJournal } from "./quest-journal";
import { dialogueSystem } from "./dialogue-system";
import { moralitySystem } from "./morality";
import { loreDiscovery } from "./lore-discovery";
import { cardGame } from "./card-game";
import { characterClasses } from "./character-classes";
import { fanFiction } from "./fan-fiction";
import { actionTemplates } from "./action-templates";
import { strategyOperations } from "./strategy-operations";
import { research } from "./research";
import { influence } from "./influence";
import { conditions } from "./conditions";
import { savingThrows } from "./saving-throws";
import { resting } from "./resting";
import { racialTraits } from "./racial-traits";

import { timeline } from "./timeline";
import { turnStructure } from "./turn-structure";
import { gamePhases } from "./game-phases";
import { regionMovement } from "./region-movement";
import { characterProgression } from "./character-progression";
import { economy } from "./economy";
import { encounterTables } from "./encounter-tables";
import { narratorProfile } from "./narrator-config";
import { gameDirector } from "./director-config";
import { messageEditing } from "./message-editing";
import { customRules } from "./custom-rules";
import { multilingual } from "./multilingual";
import { worldMap } from "./world-map";
import { playerInteractions } from "./player-interactions";
import { povSwitch } from "./pov-switch";
import { customAI } from "./custom-ai";
import { reactiveWorld } from "./reactive-world";
import { questSystem } from "./quest_system";

/* ── Sub-group assignments (keeps individual mechanic files clean) ── */
const SUB_GROUP_MAP: Record<string, MechanicSubGroup> = {
  // Systems → Combat & RPG
  combat: "combat_rpg", conditions: "combat_rpg", saving_throws: "combat_rpg",
  resting: "combat_rpg", racial_traits: "combat_rpg",
  // Systems → Character
  character_classes: "character", character_progression: "character", action_templates: "character",
  // Systems → World Systems
  time_passage: "world_systems", region_movement: "world_systems", world_map: "world_systems", timeline: "world_systems", reactive_world: "world_systems",
  // Systems → Strategy & Management
  strategy_operations: "strategy", economy: "strategy", quest_system: "strategy",
  // Systems → Exploration & Survival
  inventory: "exploration", survival: "exploration", research: "exploration",
  // Systems → Social & Special
  skill_checks: "social", reputation: "social", magic_system: "social",
  card_game: "social", player_interactions: "social", pov_switch: "social", custom_rules: "social",
  // Narrative → Narrative Flow
  quest_journal: "narrative_flow", dialogue_system: "narrative_flow",
  // Narrative → Tone & Style
  morality: "tone_style",
  // Narrative → AI Behavior
  lore_discovery: "ai_behavior",
  fan_fiction: "ai_behavior", influence: "ai_behavior",
};

// Master registry of all available mechanics
export const MECHANIC_REGISTRY: MechanicDefinition[] = [
  // Core — foundational engine behavior and shared session scaffolding
  turnStructure,
  gamePhases,
  messageEditing,
  customAI,
  reactiveWorld,
  encounterTables, // Story planning & event ripple system (optional but enhances narrative)
  // Systems — playable rulesets with state tracking, tools, or hard consequences
  // Combat & RPG
  combat, conditions, savingThrows, resting, racialTraits,
  // Character
  characterClasses, characterProgression, actionTemplates,
  // World Systems
  timePassage, regionMovement, worldMap, timeline,
  // Strategy & Management
  strategyOperations, economy, questSystem,
  // Exploration & Survival
  inventory, survival, research,
  // Social & Special
  skillChecks, reputation, magicSystem, cardGame,
  playerInteractions, povSwitch, customRules,
  // Narrative — story-shaping guidance, narrator controls, and director behavior
  // Narrative Flow
  questJournal, dialogueSystem,
  // Tone & Style
  moralitySystem,
  // AI Behavior
  loreDiscovery, fanFiction, influence,
  // Narration controls
  narratorProfile,
  multilingual,
  // Director controls
  gameDirector,
].map(m => ({ ...m, subGroup: SUB_GROUP_MAP[m.id] || m.subGroup }));

export const CATEGORY_META: Record<MechanicCategory, { label: string; description: string }> = {
  core: { label: "Core", description: "Foundational engine behavior that defines flow, orchestration, and shared session scaffolding" },
  systems: { label: "Systems", description: "Playable mechanics with state, tools, and concrete gameplay consequences" },
  narrative: { label: "Narrative", description: "Story-shaping guidance covering narration, pacing, tone, and director controls" },
};

export const SUB_GROUP_META: Record<MechanicSubGroup, { label: string; icon: string }> = {
  combat_rpg: { label: "Combat & RPG", icon: "Swords" },
  character: { label: "Character", icon: "User" },
  world_systems: { label: "World Systems", icon: "Globe" },
  strategy: { label: "Strategy & Management", icon: "Building2" },
  exploration: { label: "Exploration & Survival", icon: "Compass" },
  social: { label: "Social & Special", icon: "Users" },
  narrative_flow: { label: "Narrative Flow", icon: "BookOpen" },
  tone_style: { label: "Tone & Style", icon: "Palette" },
  ai_behavior: { label: "AI Behavior", icon: "Brain" },
};

// Legacy ID aliases for backward compatibility
const MECHANIC_ALIASES: Record<string, string> = {
  turn_structure: "time_passage",
  narrator_style: "narrator_profile",
  narrator_persona: "narrator_profile",
  narrator_descriptions: "narrator_profile",
  narrative_tone: "narrator_profile",
  response_length: "narrator_profile",
  inner_voice: "narrator_profile",
  gm_personality: "game_director",
  gm_rules_enforcement: "game_director",
  gm_pacing: "game_director",
  gm_chat: "game_director",
  difficulty_director: "game_director",
  encounter_pacing: "game_director",
  organization: "strategy_operations",
  territory: "strategy_operations",
  resources: "strategy_operations",
  consequence_engine: "reactive_world",
  butterfly_engine: "reactive_world",
  world_reactivity: "reactive_world",
};

export const getMechanicById = (id: string): MechanicDefinition | undefined => {
  const resolvedId = MECHANIC_ALIASES[id] || id;
  return MECHANIC_REGISTRY.find((m) => m.id === resolvedId);
};

// ─── AI Catalog ───

/** Returns a compact catalog of all mechanics for the builder AI to plan with */
export function getMechanicCatalog() {
  return MECHANIC_REGISTRY.map((m) => ({
    id: m.id,
    name: m.name,
    description: m.description,
    category: m.category,
    icon: m.icon,
    toolCount: m.tools.length,
    hasWidgets: !!m.widgets?.length,
    hasPostTurnHook: !!m.postTurnHook,
    hasEventReactions: !!m.onEvent,
    configKeys: m.configSchema.map((f) => f.key),
    ...(m.aiHints ? {
      purpose: m.aiHints.purpose,
      whenToUse: m.aiHints.whenToUse,
      synergies: m.aiHints.synergies,
      conflicts: m.aiHints.conflicts,
      prerequisites: m.aiHints.prerequisites,
      tags: m.aiHints.tags,
      complexity: m.aiHints.complexity,
      exampleConfig: m.aiHints.exampleConfig,
      genrePresets: m.aiHints.genrePresets,
      stateVariables: m.aiHints.stateVariables,
    } : {}),
  }));
}

// ─── Mechanic Test Harness ───

/** Test a mechanic by generating its system prompt section and initial state with sample config */
export function testMechanic(mechanicId: string, testConfig?: Record<string, any>) {
  const def = getMechanicById(mechanicId);
  if (!def) return { error: `Mechanic "${mechanicId}" not found` };

  const config = testConfig || def.aiHints?.exampleConfig || {};
  
  // Fill in defaults from configSchema for missing keys
  const fullConfig = { ...config };
  for (const field of def.configSchema) {
    if (!(field.key in fullConfig)) {
      fullConfig[field.key] = field.default;
    }
  }

  const systemPrompt = def.systemPromptSection(fullConfig);
  const initialState = def.initializeState ? def.initializeState(fullConfig) : {};
  const tools = def.tools.map((t) => ({ name: t.function.name, description: t.function.description, params: Object.keys(t.function.parameters?.properties || {}) }));
  const widgets = (def.widgets || []).map((w) => ({ id: w.id, type: w.type, component: w.component, bindings: w.dataBindings }));

  // Test postTurnHook with mock state if available
  let postTurnResult = null;
  if (def.postTurnHook) {
    const mockState = { variables: initialState, player_character: { stats: { STR: 14, DEX: 12, CON: 10, INT: 16, WIS: 13, CHA: 8 }, inventory: [] } };
    try {
      postTurnResult = def.postTurnHook({ adventureId: "test", worldId: "test", state: mockState, config: fullConfig, supabase: null });
    } catch (e: any) {
      postTurnResult = { error: e.message };
    }
  }

  // Test onEvent with a sample event if available
  let eventResult = null;
  if (def.onEvent) {
    const sampleEvents = [
      { type: "combat_kill", source: "combat", data: { target: "Goblin", xp_value: 25 } },
      { type: "quest_complete", source: "quest_journal", data: { quest_name: "Test Quest", xp_value: 50 } },
      { type: "item_acquired", source: "inventory", data: { item_name: "Test Sword" } },
    ];
    for (const evt of sampleEvents) {
      try {
        const r = def.onEvent(evt, { adventureId: "test", worldId: "test", state: { variables: initialState }, config: fullConfig, supabase: null });
        if (r) { eventResult = { event: evt.type, reaction: r }; break; }
      } catch { /* skip */ }
    }
  }

  return {
    mechanicId: def.id,
    name: def.name,
    category: def.category,
    configUsed: fullConfig,
    systemPromptPreview: systemPrompt.slice(0, 1500) + (systemPrompt.length > 1500 ? "\n... [truncated]" : ""),
    systemPromptLength: systemPrompt.length,
    initialState,
    tools,
    widgets,
    postTurnResult,
    eventReactionSample: eventResult,
  };
}

// Given active mechanic configs, build merged prompt sections and tools
export function buildMechanicsContext(activeMechanics: MechanicConfig[]) {
  const promptSections: string[] = [];
  const tools: any[] = [];

  for (const mc of activeMechanics) {
    const def = getMechanicById(mc.id);
    if (!def) continue;
    promptSections.push(def.systemPromptSection(mc.config || {}));
    tools.push(...def.tools);
  }

  return { promptSections, tools };
}

// ─── State Initialization ───

/**
 * Merge default state variables from all active mechanics.
 * Existing state values are NOT overwritten — only missing keys are filled.
 */
export function initializeMechanicState(
  activeMechanics: MechanicConfig[],
  existingVariables: Record<string, any> = {},
): Record<string, any> {
  const merged = { ...existingVariables };
  for (const mc of activeMechanics) {
    const def = getMechanicById(mc.id);
    if (!def?.initializeState) continue;
    const defaults = def.initializeState(mc.config || {});
    for (const [key, val] of Object.entries(defaults)) {
      if (!(key in merged)) {
        merged[key] = val;
      }
    }
  }
  return merged;
}

// ─── Event Propagation ───

/**
 * Propagate a MechanicEvent through all active mechanics' onEvent handlers.
 * Returns accumulated state updates and narrative notes.
 */
export function propagateEvent(
  event: MechanicEvent,
  activeMechanics: MechanicConfig[],
  context: { state: any; config?: any },
): { stateUpdates: Record<string, any>; narrativeNotes: string[] } {
  const allUpdates: Record<string, any> = {};
  const allNotes: string[] = [];

  for (const mc of activeMechanics) {
    if (mc.id === event.source) continue; // don't react to own events
    const def = getMechanicById(mc.id);
    if (!def?.onEvent) continue;
    const reaction = def.onEvent(event, {
      adventureId: "",
      worldId: "",
      state: context.state,
      config: mc.config || {},
      supabase: null,
    });
    if (reaction) {
      if (reaction.stateUpdates) Object.assign(allUpdates, reaction.stateUpdates);
      if (reaction.narrativeNotes) allNotes.push(...reaction.narrativeNotes);
    }
  }
  return { stateUpdates: allUpdates, narrativeNotes: allNotes };
}

/**
 * Run postTurnHook for all active mechanics. Returns accumulated state updates.
 */
export function runPostTurnHooks(
  activeMechanics: MechanicConfig[],
  state: any,
): { stateUpdates: Record<string, any>; narrativeNotes: string[] } {
  const allUpdates: Record<string, any> = {};
  const allNotes: string[] = [];

  for (const mc of activeMechanics) {
    const def = getMechanicById(mc.id);
    if (!def?.postTurnHook) continue;
    const reaction = def.postTurnHook({
      adventureId: "",
      worldId: "",
      state,
      config: mc.config || {},
      supabase: null,
    });
    if (reaction) {
      if (reaction.stateUpdates) Object.assign(allUpdates, reaction.stateUpdates);
      if (reaction.narrativeNotes) allNotes.push(...reaction.narrativeNotes);
    }
  }
  return { stateUpdates: allUpdates, narrativeNotes: allNotes };
}

// ─── Widget Layout System ───

export interface ResolvedWidget {
  widget: WidgetDefinition;
  mechanicId: string;
  mechanicName: string;
  data: Record<string, any>;
}

export interface WidgetLayout {
  sidebar_tabs: ResolvedWidget[];
  inline_widgets: ResolvedWidget[];
  floating_widgets: ResolvedWidget[];
  status_bar_items: ResolvedWidget[];
}

/**
 * Resolve a dot-path string like "variables.hunger" or "playerChar.inventory"
 * from a context bag { state, variables, playerChar }.
 */
export function resolveWidgetData(
  dataBindings: Record<string, string>,
  context: { state: any; variables: any; playerChar: any },
): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [propKey, path] of Object.entries(dataBindings)) {
    const parts = path.split(".");
    let value: any = context;
    for (const p of parts) {
      value = value?.[p];
      if (value === undefined) break;
    }
    result[propKey] = value ?? null;
  }
  return result;
}

function hasData(data: Record<string, any>): boolean {
  return Object.values(data).some((v) => {
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return true;
  });
}

/**
 * Build the complete widget layout from active mechanics + current state.
 * Returns widgets grouped by location, sorted by priority.
 */
export function buildWidgetLayout(
  activeMechanics: MechanicConfig[],
  context: { state: any; variables: any; playerChar: any },
): WidgetLayout {
  const layout: WidgetLayout = {
    sidebar_tabs: [],
    inline_widgets: [],
    floating_widgets: [],
    status_bar_items: [],
  };

  const seenMechanics = new Set<string>();
  for (const mc of activeMechanics) {
    const resolvedId = MECHANIC_ALIASES[mc.id] || mc.id;
    if (seenMechanics.has(resolvedId)) continue;
    seenMechanics.add(resolvedId);
    const def = getMechanicById(mc.id);
    if (!def?.widgets) continue;

    for (const widget of def.widgets) {
      const data = resolveWidgetData(widget.dataBindings, context);

      // Visibility check
      if (widget.visibility === "when_data" && !hasData(data)) continue;

      const resolved: ResolvedWidget = {
        widget,
        mechanicId: mc.id,
        mechanicName: def.name,
        data,
      };

      const bucket = widget.type === "sidebar_tab"
        ? "sidebar_tabs"
        : widget.type === "inline"
          ? "inline_widgets"
          : widget.type === "floating"
            ? "floating_widgets"
            : "status_bar_items";

      layout[bucket].push(resolved);
    }
  }

  // Sort each bucket by priority
  for (const key of Object.keys(layout) as (keyof WidgetLayout)[]) {
    layout[key].sort((a, b) => a.widget.priority - b.widget.priority);
  }

  return layout;
}

export type { MechanicDefinition, MechanicConfig, MechanicCategory, WidgetDefinition, MechanicEvent, EventReaction };
